import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Popover, PopoverHeader, PopoverBody } from 'reactstrap';
import { orderStatusFlow, cancelledStatus, OrderConditionType } from './orderStatusFlowData';
import PolicyChecker from '../../../_metronic/helpers/components/PolicyChecker';
import { enumPolicies } from '../../../_metronic/helpers';

import { Tooltip } from 'bootstrap';

const OrderStatusFlow = ({ currentStatus, nextStates = [], onStateClick, conditions = [] }) => {
    const intl = useIntl();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [popoverTarget, setPopoverTarget] = useState(null);
    const currentIndex = orderStatusFlow.indexOf(currentStatus);
    const isCancelled = currentStatus === cancelledStatus;
    const popoverContentRef = React.useRef(null);

    const togglePopover = () => setPopoverOpen(!popoverOpen);

    const handleActionClick = (triggerId) => {
        if (onStateClick) {
            onStateClick(triggerId);
        }
        setPopoverOpen(false);
    };

    const activeStatusRef = (node) => {
        if (node) {
            setPopoverTarget(node);
        }
    };

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverOpen &&
                popoverTarget &&
                !popoverTarget.contains(event.target) &&
                popoverContentRef.current &&
                !popoverContentRef.current.contains(event.target)) {
                setPopoverOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [popoverOpen, popoverTarget]);

    React.useEffect(() => {
        // Dispose any existing tooltips first to prevent conflicts
        const existingTooltips = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        existingTooltips.forEach(el => {
            const existingInstance = Tooltip.getInstance(el);
            if (existingInstance) {
                try {
                    existingInstance.dispose();
                } catch (e) {
                    // Ignore errors from tooltips in the middle of animations
                    console.warn('Tooltip disposal warning:', e);
                }
            }
        });

        // Create new tooltips
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        const tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });

        return function cleanup() {
            tooltipList.forEach(t => {
                try {
                    t.dispose();
                } catch (e) {
                    // Ignore errors from tooltips in the middle of animations
                    console.warn('Tooltip disposal warning:', e);
                }
            });
        };
    }, [conditions]);

    const renderConditionIcon = (condition) => {
        let iconClass = '';
        let tooltipId = `condition-${condition.type}-${condition.status}`;
        let tooltipText = '';

        switch (condition.type) {
            case OrderConditionType.AllItemsReady:
                iconClass = 'ki-duotone ki-check-square fs-2';
                tooltipText = condition.isMet ? intl.formatMessage({ id: 'ORDER.CONDITION.ALL_ITEMS_READY' }) : intl.formatMessage({ id: 'ORDER.CONDITION.WAITING_ITEMS' });
                break;
            case OrderConditionType.DigitalSignature:
                iconClass = 'ki-duotone ki-pencil fs-2';
                tooltipText = condition.isMet ? intl.formatMessage({ id: 'ORDER.CONDITION.SIGNATURE_DONE' }) : intl.formatMessage({ id: 'ORDER.CONDITION.WAITING_SIGNATURE' });
                break;
            case OrderConditionType.PhotosAdded:
                iconClass = 'ki-duotone ki-picture fs-2';
                tooltipText = condition.isMet ? intl.formatMessage({ id: 'ORDER.CONDITION.PHOTOS_ADDED' }) : intl.formatMessage({ id: 'ORDER.CONDITION.WAITING_PHOTOS' });
                break;
            default:
                return null;
        }

        return (
            <div
                key={`${condition.type}-${condition.isMet}`}
                className={`condition-icon ${condition.isMet ? 'text-success' : 'text-gray-400'} me-1`}
                data-bs-toggle="tooltip"
                title={tooltipText}
            >
                <i className={`${iconClass} ${condition.isMet ? 'text-success' : 'text-gray-400'}`}>
                    <span className="path1"></span>
                    <span className="path2"></span>
                </i>
            </div>
        );
    };

    return (
        <div className="order-status-flow">
            {orderStatusFlow.map((status, index) => {
                let statusClass = 'status-step';
                const isCurrent = index === currentIndex;

                const isLastStatus = currentIndex === orderStatusFlow.length - 1;

                if (index < currentIndex || isLastStatus) {
                    statusClass += ' completed';
                } else if (isCurrent) {
                    statusClass += ' active';
                }

                const statusConditions = conditions.filter(c => c.status === status);

                return (
                    <React.Fragment key={status}>
                        <div
                            className={statusClass}
                            ref={isCurrent ? activeStatusRef : null}
                            onClick={isCurrent ? togglePopover : undefined}
                        >
                            <div className="status-dot"></div>
                            <div className="status-label">{intl.formatMessage({ id: `ORDER.STATUS.${status}` })}</div>

                            {statusConditions.length > 0 && (
                                <div className="status-conditions d-flex justify-content-center mt-2">
                                    {statusConditions.map(renderConditionIcon)}
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                );
            })}
            {isCancelled && (
                <>
                    <div className="status-step cancelled active">
                        <div className="status-dot"></div>
                        <div className="status-label">{intl.formatMessage({ id: `ORDER.STATUS.${cancelledStatus}` })}</div>
                    </div>
                </>
            )}
            {popoverTarget && nextStates.length > 0 && (
                <PolicyChecker policies={[enumPolicies.OrderAdmin]}>
                    <Popover
                        placement="bottom"
                        isOpen={popoverOpen}
                        target={popoverTarget}
                        toggle={togglePopover}
                    >
                        <PopoverHeader>{intl.formatMessage({ id: 'ORDER.STATUS.AVAILABLE_ACTIONS' })}</PopoverHeader>
                        <PopoverBody>
                            <div ref={popoverContentRef}>
                                <ul className="action-list">
                                    {nextStates.map(action => (
                                        <li key={action.triggerId} onClick={() => handleActionClick(action.triggerId)}>
                                            <i className="ki-duotone ki-double-arrow-next fs-3"></i>
                                            {intl.formatMessage({ id: `ORDER.STATUS.ACTION.${action.triggerName}` })}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </PopoverBody>
                    </Popover>
                </PolicyChecker>
            )}
        </div>
    );
};

export default OrderStatusFlow;
